import React, { useState } from 'react';
import { IconCaretDown } from '@belong/icons';
import { ButtonBase, Text } from '@belong/ui';
import {
  HomeDetailInfoModel,
  MonthlyStatementDetailInfoModel,
  MonthSummaryInfoModel,
} from 'api/models.v2';
import cx from 'classnames';
import GrayBorderedBox from 'components/GrayBorderedBox/GrayBorderedBox';
import AccountPageSection from '../Pages/AccountPage/AccountPageSection/AccountPageSection';
import { EARNINGS_STRINGS } from '../Pages/YourPortfolio/Earnings/earnings.strings';
import { PastMonth } from './past-month/past-month';

type PastMonthsSectionProps = {
  // Nullable because `Earnings` reads it straight off the store, where it is
  // `null` until the fetch resolves. `getAllDetails` already reads it through
  // `?.`.
  balances: MonthlyStatementDetailInfoModel[] | null;
  selectedHome: string;
  hasManyHomes: boolean;
  handleShowPastMonthModal: (transaction: PastMonthDetail) => void;
  isResident?: boolean;
  address?: string;
};

// Exported so `Earnings` can type the detail it holds in state between this
// section handing one over and `PastMonthModal` receiving it back.
export type PastMonthDetail = HomeDetailInfoModel & {
  monthSummaryInfo: MonthSummaryInfoModel;
};

type Ref = HTMLDivElement;

function getAllDetails(
  months: MonthlyStatementDetailInfoModel[] | null,
  selectedHome: string,
): PastMonthDetail[] {
  const details: PastMonthDetail[] = [];

  months?.forEach((month) => {
    const monthSummaryInfo = month.monthSummaryInfo;

    if (!monthSummaryInfo) {
      return;
    }

    let homeDetails: HomeDetailInfoModel[] = [];

    if (selectedHome === 'all') {
      homeDetails = month.homesDetails ?? [];
    } else if (month.homesDetails?.[0]) {
      homeDetails.push(month.homesDetails[0]);
    }

    details.push(
      ...homeDetails.map((detail) => ({
        ...detail,
        monthSummaryInfo,
      })),
    );
  });

  details.sort((a, b) => {
    if (a.monthSummaryInfo.year < b.monthSummaryInfo.year) {
      return 1;
    }

    if (a.monthSummaryInfo.year > b.monthSummaryInfo.year) {
      return -1;
    }

    if (a.monthSummaryInfo.month < b.monthSummaryInfo.month) {
      return 1;
    }

    if (a.monthSummaryInfo.month > b.monthSummaryInfo.month) {
      return -1;
    }

    return 1;
  });

  return details;
}

export const PastMonthsSection = React.forwardRef<Ref, PastMonthsSectionProps>(
  function PastMonthsSection(
    {
      balances,
      selectedHome,
      hasManyHomes,
      handleShowPastMonthModal,
      address = '',
      isResident = false,
    },
    ref,
  ) {
    const details = getAllDetails(balances, selectedHome);

    const [showAllItems, setShowAllItems] = useState(false);
    const months = details.slice(0, showAllItems ? details.length : 3);
    const showMoreOrLessLabel =
      (months.length === 3 && details.length <= 3) ||
      months.length === details.length
        ? EARNINGS_STRINGS['show_less.label']
        : EARNINGS_STRINGS['show_more.label'];

    function handleToggleShowMore() {
      if (months.length === 3 && details.length > 3) {
        setShowAllItems(true);
      } else {
        setShowAllItems(false);
      }
    }

    return (
      <AccountPageSection
        title={EARNINGS_STRINGS['section.past_months.title']}
        noPaddingTop
        content={
          <div
            className={cx('pt-[20px]', {
              'transition-all ease-linear duration-hover': true,
            })}
            ref={ref}
          >
            {!details?.length ? (
              <GrayBorderedBox>
                {EARNINGS_STRINGS['section.transaction.no_history']}
              </GrayBorderedBox>
            ) : (
              months.map((month) => (
                <PastMonth
                  key={`${month.homeId}-${month.monthSummaryInfo.year}-${month.monthSummaryInfo.month}`}
                  detail={month}
                  handleShowPastMonthModal={handleShowPastMonthModal}
                  showAddress={!isResident && hasManyHomes}
                  address={address}
                />
              ))
            )}
            {details?.length > 3 ? (
              <div className="mb-sm text-right">
                <ButtonBase onClick={handleToggleShowMore}>
                  <footer className="mt-sm flex justify-end">
                    <Text fontWeight="semibold">{showMoreOrLessLabel}</Text>
                    <IconCaretDown
                      width={15}
                      className={cx(
                        'ml-xs transition-transform duration-hover ease-linear',
                        {
                          'rotate-180':
                            showMoreOrLessLabel ===
                            EARNINGS_STRINGS['show_less.label'],
                        },
                      )}
                    />
                  </footer>
                </ButtonBase>
              </div>
            ) : null}
          </div>
        }
      />
    );
  },
);
